/**
 * useTimeWindowSync — wires the time-window store to the URL
 * query string. Mount once near the app root (the AppShell
 * is the natural home — it wraps every authenticated page).
 *
 * Sync behavior:
 *
 *   URL → store: on route change, if `?window=...` is present and
 *     valid, update the store via `setWindowFromUrl` (no
 *     localStorage write — see store comments for the rationale).
 *
 *   store → URL: when the store's window changes, mirror it into
 *     the URL via `replace: true` so back/forward navigation isn't
 *     polluted with intermediate states.
 *
 *   No `?window=` in URL → no URL write. The store's default
 *     (localStorage or `30d`) is in effect but doesn't appear in
 *     the URL until the user explicitly picks a window. This keeps
 *     normal navigation clean.
 */

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { isTimeWindow } from "./types";
import { useTimeWindowStore } from "./store";

const URL_PARAM = "window";

export function useTimeWindowSync(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeWindow = useTimeWindowStore((s) => s.window);
  const setWindowFromUrl = useTimeWindowStore((s) => s.setWindowFromUrl);

  // URL → store. Runs on every search-param change so back/forward
  // and intra-app links with `?window=` are honoured.
  useEffect(() => {
    const fromUrl = searchParams.get(URL_PARAM);
    if (fromUrl && isTimeWindow(fromUrl) && fromUrl !== storeWindow) {
      setWindowFromUrl(fromUrl);
    }
    // We don't depend on `storeWindow` here — that would create a
    // loop with the store-to-URL effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setWindowFromUrl]);

  // store → URL. Only writes when the store value differs from
  // what's already in the URL — otherwise we'd push a redundant
  // history entry on every state change that already matched.
  useEffect(() => {
    const fromUrl = searchParams.get(URL_PARAM);
    if (fromUrl === storeWindow) return;
    // No write when the URL has no window param AND we're at the
    // default — keep navigation clean.
    if (!fromUrl) return;
    const next = new URLSearchParams(searchParams);
    next.set(URL_PARAM, storeWindow);
    setSearchParams(next, { replace: true });
    // Same loop-avoidance as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeWindow]);
}
